/**
 * MyCaptures.js — route owner for /captures and /intelligence.
 *
 * ⚠️ READ THIS FIRST (Lane 826). This file now decides WHICH page renders:
 *
 *   INTELLIGENCE_PORTAL_ENABLED on  -> pages/IntelligencePortal.js, the surface
 *       listing everything a human has contributed across ALL channels.
 *   INTELLIGENCE_PORTAL_ENABLED off -> the legacy chat-only capture list below,
 *       UNCHANGED.
 *
 * The legacy view is kept rather than deleted so the flag is a real rollback:
 * with it off, /captures behaves exactly as it did before this lane, including
 * the `?candidate_id=X` provenance deep link that utils/provenanceChip.js
 * builds and Home.provenance.test.js asserts. That link is a live contract
 * older than this lane, so it must work in BOTH flag states — which is also why
 * /captures stays registered unconditionally in App.js while /intelligence is
 * gated.
 *
 * Everything below this banner is the original D.5b implementation.
 *
 * --- original header ---
 *
 * D.5b "My Captures" route (/captures).
 *
 * User-scoped list (JWT company_id + user_id, Rule 106) of chat-capture
 * candidates with state. Backend endpoint GET /api/chat/capture/candidates
 * returns only USER_VISIBLE_STATUSES (pending / auto_captured / user_confirmed
 * / user_edited) — retracted, dismissed, disabled, below_threshold, pii_rejected,
 * non_intelligence, and error are filtered out server-side and never surface
 * here.
 *
 * D.7: deep-link handling. /captures?candidate_id=X scrolls the matching row
 * into view and applies a 2-second background tint before reverting. Missing
 * or unknown id renders the page normally with no error.
 *
 * Post-action state policy (from Section 1 Issue 3 of the plan):
 *   - Optimistic local updates mirror Home.js.
 *   - "Local ≡ server for every field that drives the UI" — what we send to
 *     editCandidate IS what backend stored (JSONB pass-through).
 *   - On edit save, entity_id_resolved is preserved locally (A.i). Backend
 *     may re-resolve; badge reflects last known state until next page refresh.
 *
 * Tests: MyCaptures.deeplink.test.js (D.7) covers the deep-link path.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import IntelligencePortal from './IntelligencePortal';
import { useToast } from '../components/ToastHost';
import CaptureChip from '../components/CaptureChip';
import CaptureEditModal from '../components/CaptureEditModal';
import { colors } from '../components/AppLayout';
import { formatChipLabel } from '../utils/captureLabel';
import {
  listCaptures,
  confirmCandidate,
  dismissCandidate,
  retractCandidate,
} from '../api/chatCapture';


const PAGE_SIZE = 50;
const CAPTURED_STATUSES = new Set(['auto_captured', 'user_confirmed', 'user_edited']);


// ============================================================================
// Pure helpers — key mapping, enrichment, predicates
// ============================================================================

// Map backend row shape → CaptureChip / CaptureEditModal payload shape.
// Backend: entity_name_raw / predicate_proposed / value_proposed.
// Frontend: entity_name / predicate / value.
function rowToDisplayPayload(row) {
  return {
    entity_type: row.entity_type || null,
    entity_name: row.entity_name_raw || '',
    predicate: row.predicate_proposed || null,
    value: row.value_proposed && typeof row.value_proposed === 'object' ? row.value_proposed : {},
  };
}

function enrichRow(row) {
  const displayPayload = rowToDisplayPayload(row);
  return { ...row, displayPayload, label: formatChipLabel(displayPayload) };
}

function isUnresolved(row) {
  return row.entity_id_resolved == null && row.entity_type != null;
}

function matchesFilter(row, filter) {
  if (filter === 'all') return true;
  if (filter === 'pending') return row.status === 'pending';
  if (filter === 'captured') return CAPTURED_STATUSES.has(row.status);
  return true;
}


// ============================================================================
// Component
// ============================================================================

function MyCaptures() {
  const { intelligencePortalEnabled } = useAuth();
  // The flag decides which page this route renders. Checked BEFORE any state
  // or effect below, so the legacy list does no fetching when the portal is on.
  if (intelligencePortalEnabled) return <IntelligencePortal />;
  return <LegacyCaptures />;
}


function LegacyCaptures() {
  // user + company scoping is enforced server-side via JWT; no client-side
  // filtering needed here.
  const { chatCaptureEnabled } = useAuth();
  const { showToast } = useToast();
  const [searchParams] = useSearchParams();

  const [items, setItems] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [initialError, setInitialError] = useState(null);
  const [moreError, setMoreError] = useState(null);
  const [filter, setFilter] = useState('all');
  const [editingCandidate, setEditingCandidate] = useState(null);

  // D.7 deep-link state. targetCandidateId is parsed once per render from
  // the URL; highlightedId is set briefly on match to drive the row tint.
  const targetCandidateId = (() => {
    const raw = searchParams.get('candidate_id');
    if (raw == null || raw === '') return null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  })();
  const rowRefs = useRef(new Map());
  const highlightTimeoutRef = useRef(null);
  const [highlightedId, setHighlightedId] = useState(null);

  const setRowRef = useCallback((candidateId) => (node) => {
    if (node) {
      rowRefs.current.set(candidateId, node);
    } else {
      rowRefs.current.delete(candidateId);
    }
  }, []);

  // ---- Fetch ----

  const fetchPage = useCallback(async (cursor) => {
    return listCaptures({ cursor, limit: PAGE_SIZE });
  }, []);

  const reload = useCallback(async () => {
    setLoading(true);
    setInitialError(null);
    setMoreError(null);
    try {
      const res = await fetchPage(null);
      const enriched = (res.items || []).map(enrichRow);
      setItems(enriched);
      setNextCursor(res.next_cursor ?? null);
    } catch (err) {
      console.log('[CHAT-CAPTURE-LIST-ERROR]', err?.status, err?.detail);
      setInitialError('Couldn\u2019t load your captures.');
    } finally {
      setLoading(false);
    }
  }, [fetchPage]);

  useEffect(() => { reload(); }, [reload]);

  // D.7 deep-link effect: after items load and a candidate_id is requested,
  // scroll the matching row into view and apply a 2-second tint. Missing or
  // unknown id is a silent no-op (row may have been retracted between chip
  // render and click). Cleanup clears the timeout on unmount or rerun to
  // dodge setState-on-unmounted warnings.
  useEffect(() => {
    if (loading || targetCandidateId == null || items.length === 0) return undefined;
    const found = items.find(r => Number(r.candidate_id) === targetCandidateId);
    if (!found) return undefined;

    const node = rowRefs.current.get(found.candidate_id);
    if (node && typeof node.scrollIntoView === 'function') {
      node.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    setHighlightedId(found.candidate_id);
    highlightTimeoutRef.current = setTimeout(() => {
      setHighlightedId(null);
      highlightTimeoutRef.current = null;
    }, 2000);

    return () => {
      if (highlightTimeoutRef.current) {
        clearTimeout(highlightTimeoutRef.current);
        highlightTimeoutRef.current = null;
      }
    };
  }, [loading, items, targetCandidateId]);

  const loadMore = useCallback(async () => {
    if (nextCursor == null || loadingMore) return;
    setLoadingMore(true);
    setMoreError(null);
    try {
      const res = await fetchPage(nextCursor);
      const enriched = (res.items || []).map(enrichRow);
      setItems(prev => prev.concat(enriched));
      setNextCursor(res.next_cursor ?? null);
    } catch (err) {
      console.log('[CHAT-CAPTURE-LIST-LOADMORE-ERROR]', err?.status, err?.detail);
      setMoreError('Couldn\u2019t load more. Try again.');
    } finally {
      setLoadingMore(false);
    }
  }, [nextCursor, loadingMore, fetchPage]);

  // ---- Optimistic row mutations ----

  const mutateRow = useCallback((candidateId, patch) => {
    setItems(prev => prev.map(r => {
      if (r.candidate_id !== candidateId) return r;
      const merged = { ...r, ...patch };
      const touchesLabel = 'entity_name_raw' in patch
                        || 'predicate_proposed' in patch
                        || 'value_proposed' in patch;
      return touchesLabel ? enrichRow(merged) : merged;
    }));
  }, []);

  const removeRow = useCallback((candidateId) => {
    setItems(prev => prev.filter(r => r.candidate_id !== candidateId));
  }, []);

  // ---- Action handlers ----

  const handleConfirm = useCallback(async (candidateId) => {
    try {
      await confirmCandidate(candidateId);
      mutateRow(candidateId, { status: 'user_confirmed' });
    } catch (err) {
      if (err?.status === 409) {
        console.log('[CHAT-CAPTURE-LIST-CONFIRM-409]', candidateId);
        mutateRow(candidateId, { uiAlreadySaved: true });
        return;
      }
      console.log('[CHAT-CAPTURE-LIST-CONFIRM-ERROR]', err?.status, err?.detail);
      showToast({ message: 'Couldn\u2019t save. Try again.', variant: 'error', durationMs: 4000 });
    }
  }, [mutateRow, showToast]);

  const handleEditOpen = useCallback((row) => {
    setEditingCandidate({ candidateId: row.candidate_id, payload: row.displayPayload });
  }, []);

  const handleEditClose = useCallback(() => {
    setEditingCandidate(null);
  }, []);

  // Modal calls onSaveSuccess with { entity_type, entity_name, predicate, value }
  // — the values it just sent to editCandidate. Patch the row with those,
  // preserving entity_id_resolved (A.i).
  const handleEditSaveSuccess = useCallback((sentEdits) => {
    const target = editingCandidate;
    if (!target) return;
    mutateRow(target.candidateId, {
      status: 'user_edited',
      entity_name_raw: sentEdits.entity_name,
      predicate_proposed: sentEdits.predicate,
      value_proposed: sentEdits.value,
    });
    setEditingCandidate(null);
  }, [editingCandidate, mutateRow]);

  const handleEdit409 = useCallback(() => {
    const target = editingCandidate;
    if (!target) return;
    mutateRow(target.candidateId, { uiAlreadySaved: true });
    setEditingCandidate(null);
  }, [editingCandidate, mutateRow]);

  const handleDismiss = useCallback(async (candidateId) => {
    // Optimistic — user intent is to not save. Silent on failure (intentional,
    // matches Home.js): row reappears on next page load if the DELETE missed.
    removeRow(candidateId);
    try {
      await dismissCandidate(candidateId);
    } catch (_) {
      // Deliberate silent swallow.
    }
  }, [removeRow]);

  const handleUndoRetract = useCallback(async (candidateId) => {
    try {
      await retractCandidate(candidateId);
      removeRow(candidateId);
    } catch (err) {
      console.log('[CHAT-CAPTURE-LIST-RETRACT-ERROR]', err?.status, err?.detail);
      showToast({ message: 'Could not undo. Already handled.', variant: 'error', durationMs: 4000 });
    }
  }, [removeRow, showToast]);

  // ---- Derived values ----

  const visibleRows = items.filter(r => matchesFilter(r, filter));
  const showFooterHint = filter !== 'all' && nextCursor !== null;

  // ---- Render ----

  return (
    <div style={{ padding: '32px', maxWidth: '960px', margin: '0 auto' }}>
      <CaptureEditModal
        open={editingCandidate !== null}
        candidateId={editingCandidate?.candidateId}
        initialPayload={editingCandidate?.payload}
        onClose={handleEditClose}
        onSaveSuccess={handleEditSaveSuccess}
        on409={handleEdit409}
      />

      <div style={{ marginBottom: '24px' }}>
        <h1 style={{
          fontSize: '24px', fontWeight: 700, color: colors.darkText, margin: 0,
        }}>
          My Captures
        </h1>
        <p style={{
          fontSize: 'var(--font-size-sm, 13px)', color: colors.textMuted,
          margin: '4px 0 0 0',
        }}>
          Intelligence saved from your AI chat messages.
        </p>
      </div>

      <FilterPills value={filter} onChange={setFilter} />

      <div style={{
        background: colors.white,
        border: `1px solid ${colors.mediumGray}`,
        borderRadius: '12px',
        padding: '8px 0',
        minHeight: '240px',
      }}>
        {renderBody({
          loading, initialError, reload, items, visibleRows,
          chatCaptureEnabled, filter,
          onConfirm: handleConfirm,
          onEditOpen: handleEditOpen,
          onDismiss: handleDismiss,
          onUndoRetract: handleUndoRetract,
          setRowRef,
          highlightedId,
        })}

        {(!loading && !initialError && items.length > 0 && nextCursor !== null) && (
          <div style={{
            padding: '16px 24px', borderTop: `1px solid ${colors.mediumGray}`,
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px',
          }}>
            <button
              type="button"
              onClick={loadMore}
              disabled={loadingMore}
              style={{
                background: 'transparent',
                border: `1px solid ${colors.mediumGray}`,
                color: colors.darkText,
                padding: '6px 14px',
                borderRadius: '6px',
                fontSize: 'var(--font-size-xs, 12px)',
                fontWeight: 500,
                cursor: loadingMore ? 'wait' : 'pointer',
              }}
            >
              {loadingMore ? 'Loading\u2026' : (moreError || 'Load more')}
            </button>
            {showFooterHint && (
              <span style={{ fontSize: '11px', color: colors.textMuted }}>
                Searched the first {items.length} loaded captures. Load more to keep searching.
              </span>
            )}
          </div>
        )}
      </div>

    </div>
  );
}


// ============================================================================
// Sub-renders (kept inside file per plan — no utils extraction)
// ============================================================================

function FilterPills({ value, onChange }) {
  const options = [
    { key: 'all', label: 'All' },
    { key: 'pending', label: 'Pending' },
    { key: 'captured', label: 'Captured' },
  ];
  return (
    <div style={{ display: 'flex', gap: '6px', marginBottom: '16px' }}>
      {options.map(opt => {
        const active = value === opt.key;
        return (
          <button
            key={opt.key}
            type="button"
            onClick={() => onChange(opt.key)}
            style={{
              background: active ? colors.electricBlue : colors.white,
              color: active ? colors.white : colors.darkText,
              border: `1px solid ${active ? colors.electricBlue : colors.mediumGray}`,
              padding: '6px 14px',
              borderRadius: '100px',
              fontSize: 'var(--font-size-xs, 12px)',
              fontWeight: 500,
              cursor: 'pointer',
            }}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}


function renderBody(props) {
  const {
    loading, initialError, reload, items, visibleRows,
    chatCaptureEnabled, filter,
    onConfirm, onEditOpen, onDismiss, onUndoRetract,
    setRowRef, highlightedId,
  } = props;

  if (loading) {
    return (
      <div style={{
        padding: '48px 24px', textAlign: 'center', color: colors.textMuted,
        fontSize: 'var(--font-size-sm, 13px)',
      }}>
        Loading&hellip;
      </div>
    );
  }

  if (initialError) {
    return (
      <div style={{
        padding: '48px 24px', textAlign: 'center', color: colors.textMuted,
        fontSize: 'var(--font-size-sm, 13px)',
      }}>
        <div style={{ marginBottom: '12px', color: colors.darkText, fontWeight: 500 }}>
          {initialError}
        </div>
        <button
          type="button"
          onClick={reload}
          style={{
            background: colors.white,
            border: `1px solid ${colors.mediumGray}`,
            color: colors.darkText,
            padding: '6px 14px',
            borderRadius: '6px',
            fontSize: 'var(--font-size-xs, 12px)',
            fontWeight: 500,
            cursor: 'pointer',
          }}
        >
          Try again
        </button>
      </div>
    );
  }

  if (items.length === 0) {
    return <EmptyState chatCaptureEnabled={chatCaptureEnabled} />;
  }

  if (visibleRows.length === 0) {
    return (
      <div style={{
        padding: '48px 24px', textAlign: 'center', color: colors.textMuted,
        fontSize: 'var(--font-size-sm, 13px)',
      }}>
        No {filter} captures in the rows loaded so far.
      </div>
    );
  }

  return (
    <div>
      {visibleRows.map(row => (
        <CaptureRow
          key={row.candidate_id}
          row={row}
          onConfirm={onConfirm}
          onEditOpen={onEditOpen}
          onDismiss={onDismiss}
          onUndoRetract={onUndoRetract}
          rowRef={setRowRef ? setRowRef(row.candidate_id) : undefined}
          highlighted={highlightedId === row.candidate_id}
        />
      ))}
    </div>
  );
}


function CaptureRow({ row, onConfirm, onEditOpen, onDismiss, onUndoRetract,
                     rowRef, highlighted }) {
  const unresolved = isUnresolved(row);
  const wrapperStyle = {
    padding: '12px 24px',
    borderTop: `1px solid ${colors.mediumGray}`,
    display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap',
    // D.7 deep-link tint — brand-blue at low opacity. Transition keeps the
    // 2-second revert from snapping.
    backgroundColor: highlighted ? 'rgba(34, 81, 255, 0.08)' : undefined,
    transition: 'background-color 0.4s ease-out',
  };

  let chip = null;
  if (row.status === 'pending') {
    chip = (
      <CaptureChip
        variant="confirm"
        label={row.label}
        rationale={row.rationale}
        candidateId={row.candidate_id}
        disabled={Boolean(row.uiAlreadySaved)}
        onConfirm={() => onConfirm(row.candidate_id)}
        onEdit={() => onEditOpen(row)}
        onDismiss={() => onDismiss(row.candidate_id)}
      />
    );
  } else if (CAPTURED_STATUSES.has(row.status)) {
    chip = (
      <CaptureChip
        variant="captured"
        label={row.label}
        rationale={row.rationale}
        candidateId={row.candidate_id}
        onRetract={() => onUndoRetract(row.candidate_id)}
      />
    );
  } else {
    return null;
  }

  return (
    <div
      ref={rowRef}
      data-row-candidate-id={row.candidate_id}
      data-highlighted={highlighted ? 'true' : undefined}
      className={highlighted ? 'capture-row-highlighted' : undefined}
      style={wrapperStyle}
    >
      {chip}
      {unresolved && (
        <span
          title="This capture's entity wasn't matched to a known supplier, account, category, or material. The fact is saved but not linked."
          style={{
            fontSize: '11px', color: colors.textMuted,
            border: `1px solid ${colors.mediumGray}`,
            padding: '3px 8px', borderRadius: '100px',
            whiteSpace: 'nowrap',
          }}
        >
          Couldn&rsquo;t match
        </span>
      )}
    </div>
  );
}


function EmptyState({ chatCaptureEnabled }) {
  const cardStyle = {
    margin: '24px', padding: '32px',
    background: colors.white,
    border: `1px solid ${colors.mediumGray}`,
    borderRadius: '12px',
    color: colors.darkText,
  };
  const headingStyle = {
    fontSize: 'var(--font-size-md, 15px)', fontWeight: 600,
    marginBottom: '12px',
  };
  const paraStyle = {
    fontSize: 'var(--font-size-sm, 13px)', color: colors.textMuted,
    lineHeight: 1.6, margin: '0 0 12px 0',
  };

  if (chatCaptureEnabled === false) {
    return (
      <div style={cardStyle}>
        <div style={headingStyle}>Chat capture isn&rsquo;t on for your company yet.</div>
        <p style={paraStyle}>
          When enabled, Wingman watches your AI chat messages for supplier and market
          intelligence (price changes, lead-time shifts, capacity constraints)
          and saves them here so they can inform future answers.
        </p>
        <p style={paraStyle}>Contact your admin to turn it on.</p>
      </div>
    );
  }

  return (
    <div style={cardStyle}>
      <div style={headingStyle}>No captures yet.</div>
      <p style={paraStyle}>
        Mention facts in your AI chat and Wingman will offer to save them here. Try
        something like:
      </p>
      <ul style={{ ...paraStyle, paddingLeft: '20px' }}>
        <li><em>&ldquo;Parker raised O-ring prices 8% last January.&rdquo;</em></li>
        <li><em>&ldquo;Vulcan&rsquo;s lead time on tungsten seals went from 6 to 10 weeks.&rdquo;</em></li>
      </ul>
      <p style={paraStyle}>
        High-confidence facts are saved automatically. Lower-confidence ones show a
        &ldquo;Capture this?&rdquo; chip for you to confirm.
      </p>
    </div>
  );
}


export default MyCaptures;
